import React, { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import axios from "../../utils/axiosConfig";
import { startVoiceSampleRecording } from "../../utils/voiceSampleRecorder";
import { audioFileExtension } from "../../utils/audioRecordingType";

// Voice sample before a test with speaker monitoring (Instruction.jsx opens it from Start / Proceed
// when the test needs one and none is accepted yet). Recorded like a video interview answer: Start
// reading, read the sentence, Done (utils/voiceSampleRecorder.js); the file is uploaded, checked and
// stored by the backend (POST /test/voice-sample). A working microphone is required; once it works,
// an unrecognised reading or our checks failing never block (the backend accepts and flags).
// "Continue" starts the test from its own click (full screen needs a click).
const SENTENCE =
  "Hello, I am ready to begin my assessment today. I will answer every question myself, in my own words and in my own voice. Thank you.";

// Done is enabled once there has been time to read the whole sentence (~0.32 s a word, at least
// 8 s), so pressing it early doesn't cut the reading short (staging 22 Oct: full readings took 7-9.5 s;
// ones stopped at about 6 s missed the end and some were refused).
const SECONDS_PER_WORD = 0.32;
const MIN_SECONDS = 8;
const doneAfterSeconds = (text) => Math.max(MIN_SECONDS, Math.ceil(text.split(/\s+/).filter(Boolean).length * SECONDS_PER_WORD));
const MAX_SECONDS = 20; // stops by itself

// Our server not answering this many times in a row: the candidate continues without a sample.
const SERVER_ERROR_LIMIT = 3;

const MESSAGES = {
  sentence: "We couldn't make out the sentence. Please read it again, clearly and at a normal pace.",
  mixed_voices: "We heard more than one voice. Please read the sentence on your own, in a quiet place.",
  // Also a reading too quiet to hear (the backend counts it as silent).
  silent: "We couldn't hear you. Check your microphone isn't muted or covered, sit close to it and read a little louder.",
  denied:
    "Microphone access is blocked. Allow the microphone for this site (on iPhone: Settings > Safari > Microphone, or tap 'aA' in the address bar > Website Settings), then try again.",
  unsupported: "This browser can't record from the microphone. Please use the latest Chrome, Safari or Edge.",
  server: "We couldn't check your recording. Please try again.",
};

export default function VoiceSampleStep({ open, userexamId, sentence, onDone }) {
  const [status, setStatus] = useState("idle"); // idle | starting | recording | checking | retry | accepted
  const [message, setMessage] = useState("");
  const [seconds, setSeconds] = useState(0);
  const serverErrorsRef = useRef(0);
  const doneAfter = doneAfterSeconds(sentence || SENTENCE);
  const recordingRef = useRef(null);

  // Stop the microphone if the dialog goes away mid-recording.
  useEffect(
    () => () => {
      if (recordingRef.current) recordingRef.current.cancel();
    },
    []
  );

  const upload = async (blob) => {
    setStatus("checking");
    try {
      const formData = new FormData();
      formData.append("audiofile", blob, `voice_sample_${userexamId}_${Date.now()}.${audioFileExtension(blob.type)}`);
      formData.append("userexamid", userexamId);
      const resp = await axios.post("/test/voice-sample", formData);
      serverErrorsRef.current = 0;
      if (resp.data && resp.data.ok) {
        setStatus("accepted");
      } else {
        setMessage(MESSAGES[resp.data && resp.data.reason] || MESSAGES.sentence);
        setStatus("retry");
      }
    } catch (error) {
      console.warn("[VoiceSample] check failed:", error?.message || error);
      serverErrorsRef.current += 1;
      if (serverErrorsRef.current >= SERVER_ERROR_LIMIT) {
        setStatus("accepted"); // our outage never stops the candidate
        return;
      }
      setMessage(MESSAGES.server);
      setStatus("retry");
    }
  };

  const stop = async () => {
    const recording = recordingRef.current;
    if (!recording) return;
    recordingRef.current = null;
    upload(await recording.stop());
  };

  // Elapsed seconds while recording; stops by itself at MAX_SECONDS.
  useEffect(() => {
    if (status !== "recording") return undefined;
    const startedAt = Date.now();
    const id = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setSeconds(elapsed);
      if (elapsed >= MAX_SECONDS) stop();
    }, 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const start = async () => {
    setMessage("");
    setSeconds(0);
    setStatus("starting");
    try {
      recordingRef.current = await startVoiceSampleRecording();
      setStatus("recording");
    } catch (error) {
      console.warn("[VoiceSample] recording failed:", error?.kind, error?.message);
      setMessage(MESSAGES[error && error.kind] || MESSAGES.unsupported);
      setStatus("retry");
    }
  };

  return (
    <Dialog open={open} disableEscapeKeyDown maxWidth="sm" fullWidth>
      <DialogTitle>
        <b>Voice sample</b>
      </DialogTitle>
      <DialogContent>
        {status === "accepted" ? (
          <Typography style={{ color: "#15803d" }}>Thank you, your voice sample is recorded. Click Continue to begin.</Typography>
        ) : (
          <>
            <Typography gutterBottom>
              This test listens for other voices. Tap <b>Start reading</b>, read this sentence aloud, then tap <b>Done</b>:
            </Typography>
            <Typography
              style={{ background: "#f1f5f9", borderRadius: "8px", padding: "12px 14px", margin: "12px 0", fontSize: "1.05rem", lineHeight: 1.5 }}
            >
              {sentence || SENTENCE}
            </Typography>
            {status === "recording" && (
              <>
                <Typography variant="body2" style={{ marginBottom: "6px", color: "#b91c1c" }}>
                  ● Recording... {seconds}s
                </Typography>
                <LinearProgress variant="determinate" value={Math.min(100, Math.round((seconds / MAX_SECONDS) * 100))} />
              </>
            )}
            {(status === "checking" || status === "starting") && (
              <>
                <Typography variant="body2" style={{ marginBottom: "6px" }}>
                  {status === "checking" ? "Checking your recording..." : "Starting the microphone..."}
                </Typography>
                <LinearProgress />
              </>
            )}
            {message && (
              <Typography variant="body2" style={{ color: "#b91c1c", marginTop: "12px" }}>
                {message}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions style={{ padding: "12px 24px 20px" }}>
        {status === "accepted" ? (
          <Button variant="contained" color="primary" onClick={onDone} autoFocus>
            Continue
          </Button>
        ) : status === "recording" ? (
          <Button variant="contained" color="primary" onClick={stop} disabled={seconds < doneAfter}>
            {seconds < doneAfter ? `Done (${doneAfter - seconds}s)` : "Done"}
          </Button>
        ) : (
          <Button variant="contained" color="primary" onClick={start} disabled={status === "checking" || status === "starting"}>
            {status === "retry" ? "Try again" : "Start reading"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
